import { useState, useCallback } from 'react';
import { ImpersonationApi } from '@/services/audit.service';
import { useNotification } from '@/contexts/admin/NotificationContext';
import { useAuth, tokenIsImpersonated } from '@/contexts/shared/AuthContext';
import { tokenStorage } from '@/api/tokenManager';

import { tr } from '@/i18n';

const RETURN_KEY = 'impersonation_return_to';
const FALLBACK_RETURN = '/admin/dashboard/users/user-management';

/** Remember the admin's page when "Log in as" starts, so "Return to my account" goes back to it. */
export const rememberImpersonationReturn = () => {
  try { localStorage.setItem(RETURN_KEY, window.location.pathname + window.location.search); } catch { /* storage off */ }
};

const takeImpersonationReturn = (): string => {
  let to: string | null = null;
  try { to = localStorage.getItem(RETURN_KEY); localStorage.removeItem(RETURN_KEY); } catch { /* storage off */ }
  // Same-site paths only.
  return to && to.startsWith('/') && !to.startsWith('//') ? to : FALLBACK_RETURN;
};
export const useImpersonation = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showNotification } = useNotification();
  const { user } = useAuth();

  const isImpersonating = tokenIsImpersonated(tokenStorage.getAccessToken());

  const loginAs = useCallback(async (userId: number | string) => {
    setIsSubmitting(true);
    try {
      let adminName = 'Administrator';
      if (user) {
        adminName = `${user.firstname || ''} ${user.lastname || ''}`.trim() || 'Administrator';
      }

      const data = await ImpersonationApi.loginAs(userId);
      
      tokenStorage.clearTokens();
      
      localStorage.setItem('original_admin_name', adminName);
      localStorage.setItem('is_impersonating', 'true');
      rememberImpersonationReturn();
      
      tokenStorage.setTokens(data.access_token, data.refresh_token);

      window.location.assign('/user/dashboard');
    } catch {
      showNotification(tr("ui.failed_to_initiate_impersonation_mode", "Failed to initiate impersonation mode."), 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [showNotification, user]);

  const returnToAccount = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const data = await ImpersonationApi.returnToAccount();
      
      tokenStorage.clearTokens();
      
      localStorage.removeItem('is_impersonating');
      localStorage.removeItem('original_admin_name');
      
      tokenStorage.setTokens(data.access_token, data.refresh_token);

      window.location.assign(takeImpersonationReturn());
    } catch (error) {
      // Not in a "Log in as" session any more (it ended or expired): this already is the
      // admin's own account — drop the leftover banner instead of failing.
      if (!tokenIsImpersonated(tokenStorage.getAccessToken()) || (error as { response?: { status?: number } })?.response?.status === 400) {
        localStorage.removeItem('is_impersonating');
        localStorage.removeItem('original_admin_name');
        window.location.reload();
        return;
      }
      showNotification(tr("ui.failed_to_return_to_original_account", "Failed to return to original account."), 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [showNotification]);

  return {
    loginAs,
    returnToAccount,
    isImpersonating,
    isSubmitting
  };
};